import React, { useCallback, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { useSortable } from '@dnd-kit/sortable';

import WidgetFrame from './WidgetFrame';
import { WIDGET_BY_ID } from './registry';
import { SIZES, mobileSlotStyle } from './sizes';

const focusHeader = (id) => {
  if (typeof requestAnimationFrame === 'undefined') return;
  requestAnimationFrame(() => {
    document.querySelector(`[data-testid="widget-${id}-header"]`)?.focus();
  });
};

/**
 * One grid cell. Desktop cells are dnd-kit sortables with transforms disabled: the CSS grid
 * reflows on every order change, so the active item is drawn as a dashed ghost in its
 * current cell and the lifted copy lives in the DragOverlay.
 */
function SortableSlot({ widget, entry, mode, isMobile, activeId, actions, onOpenSettings, sx }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, isDragging } = useSortable({ id: widget.id, disabled: mode !== 'edit' });
  const handleProps = useMemo(() => ({ attributes, listeners, setActivatorNodeRef }), [attributes, listeners, setActivatorNodeRef]);
  return (
    <SlotBody
      innerRef={setNodeRef}
      widget={widget}
      entry={entry}
      mode={mode}
      isMobile={isMobile}
      ghost={activeId === widget.id || isDragging}
      handleProps={handleProps}
      actions={actions}
      onOpenSettings={onOpenSettings}
      sx={sx}
    />
  );
}

function SlotBody({ innerRef, widget, entry, mode, isMobile, ghost, handleProps, actions, onOpenSettings, sx }) {
  // 'visible' | 'collapsed' (edit mode, one-row placeholder) | 'hidden' (view mode, no slot)
  const [visibility, setVisibility] = useState('visible');
  const { id, size, opts } = widget;
  const span = SIZES[size] || SIZES.M;
  const rows = visibility === 'collapsed' ? 1 : span.rows;
  const gridStyle = isMobile ? mobileSlotStyle(entry, size) : { gridColumn: `span ${span.cols}`, gridRow: `span ${rows}` };

  const onKeyDown = useCallback(
    (event) => {
      if (mode !== 'edit') return;
      switch (event.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          event.preventDefault();
          actions.moveBy(id, -1);
          focusHeader(id);
          break;
        case 'ArrowRight':
        case 'ArrowDown':
          event.preventDefault();
          actions.moveBy(id, 1);
          focusHeader(id);
          break;
        case 'Delete':
        case 'Backspace':
          event.preventDefault();
          actions.remove(id);
          break;
        case 'Enter':
        case ' ':
          if (entry.settings?.length) {
            event.preventDefault();
            onOpenSettings(id, event.currentTarget);
          }
          break;
        default:
          break;
      }
    },
    [mode, actions, id, entry.settings, onOpenSettings]
  );

  const onResizeGroup = useCallback((group) => actions.resizeGroup(id, group), [actions, id]);
  const onRemove = useCallback(() => actions.remove(id), [actions, id]);
  const onSettings = useCallback((anchorEl) => onOpenSettings(id, anchorEl), [onOpenSettings, id]);
  const onSetOpts = useCallback((next) => actions.setOpts(id, next), [actions, id]);

  return (
    <Box ref={innerRef} data-slot-id={id} sx={{ ...gridStyle, minWidth: 0, minHeight: 0, display: visibility === 'hidden' ? 'none' : 'block', ...sx }}>
      {ghost ? (
        <Box sx={{ height: '100%', minHeight: isMobile ? 80 : undefined, border: '2px dashed #3182ce', borderRadius: '12px', bgcolor: 'rgba(49,130,206,.06)' }} />
      ) : (
        <WidgetFrame
          entry={entry}
          id={id}
          size={size}
          opts={opts}
          mode={mode}
          isMobile={isMobile}
          handleProps={handleProps}
          onKeyDown={onKeyDown}
          onResizeGroup={onResizeGroup}
          onRemove={onRemove}
          onOpenSettings={onSettings}
          onSetOpts={onSetOpts}
          onHiddenChange={setVisibility}
        />
      )}
    </Box>
  );
}

export default function WidgetSlot({ widget, mode, isMobile, sortable, activeId, actions, onOpenSettings, sx }) {
  const entry = WIDGET_BY_ID[widget.id];
  if (!entry) return null;
  if (sortable) {
    return <SortableSlot widget={widget} entry={entry} mode={mode} isMobile={isMobile} activeId={activeId} actions={actions} onOpenSettings={onOpenSettings} sx={sx} />;
  }
  return <SlotBody widget={widget} entry={entry} mode={mode} isMobile={isMobile} ghost={false} handleProps={null} actions={actions} onOpenSettings={onOpenSettings} sx={sx} />;
}
